import React, { useCallback, useEffect } from 'react';
import { Grow, Paper } from '@mui/material';
import {
  notifySMSReceivedAtom,
  notifySMSSentAtom,
} from '../../../Atoms/HubAtoms';
import { openSnackbarAdviseAtom } from '../../../Atoms';
import { useAtomValue, useSetAtom } from 'jotai';
import useAPI from '../../../api';
import ChatBox from './ChatBox';
import useInfiniteQuery from '../../../Hooks/useInfiniteQuery';
import { useIntl } from 'react-intl';

const Chat = function Chat({
  open,
  onClose,
  customerId,
  campaignId,
  branchId,
  customerUnreadMessages,
  setCustomerUnreadMessages,
  endCallMessage,
  setEndCallMessage,
  setSendEndCallMessage,
  setEndedCallData,
}) {
  const intl = useIntl();
  const API = useAPI();
  const notifySMSSent = useAtomValue(notifySMSSentAtom);
  const notifySMSReceived = useAtomValue(notifySMSReceivedAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);

  const getMessages = useCallback(
    (pageNumber, signal) => {
      return customerId
        ? API.getEntityMessages(customerId, false, {
            pageNumber,
            silent: true,
            signal,
          })
        : Promise.resolve([]);
    },
    [customerId]
  );

  const {
    items: chatMessages,
    recordCount,
    setNewItems,
    updateItem,
    ObserverWrapper,
  } = useInfiniteQuery(getMessages);

  const handleSendMessage = (newMessage) => {
    API.sendMessage(
      {
        branchId: branchId,
        recipients: [customerId], //array of guids
        message: newMessage,
      },
      (result) => {
        if (result?.length < 0) {
          setOpenSnackbarAdvise({
            message: intl.messages.SMS_ERROR,
            severity: 'error',
          });
          return;
        }
        setNewItems(result[0], recordCount + 1);
      },
      () => {
        setOpenSnackbarAdvise({
          message: intl.messages.SMS_ERROR,
          severity: 'error',
        });
      }
    );
  };

  useEffect(() => {
    //increment when 'notifySMSReceived': message is received from the client with provided id
    if (notifySMSReceived?.baseEntityId === customerId) {
      setCustomerUnreadMessages(customerUnreadMessages + 1);
      setNewItems(notifySMSReceived, recordCount + 1);
    }
  }, [notifySMSReceived?.id]);

  useEffect(() => {
    //increment when 'notifySMSSent': message is sended from the user
    if (
      notifySMSSent?.baseEntityId === customerId &&
      !chatMessages?.find((x) => x.id === notifySMSSent?.id)
    ) {
      setNewItems(notifySMSSent, recordCount + 1);
    }
  }, [notifySMSSent?.id]);

  useEffect(() => {
    if (endCallMessage) {
      if (
        (endCallMessage?.channel === 'BOTH' ||
          endCallMessage?.channel === 'SMS') &&
        endCallMessage?.message
      )
        API.sendMessage(
          {
            templateId: endCallMessage?.templateId,
            activityId: endCallMessage?.activityId,
            branchId: endCallMessage?.branchId,
            recipients: endCallMessage?.recipients,
            message: endCallMessage?.message,
          },
          (result) => {
            setOpenSnackbarAdvise({
              message: intl.messages.MESSAGE_SUCCESSFULLY_SENT,
              severity: 'success',
            });
            setNewItems(result[0], recordCount + 1);
            setEndCallMessage(null);
            setSendEndCallMessage(false);
            setEndedCallData(null);
          },
          () => {
            setOpenSnackbarAdvise({
              message: intl.messages.SMS_ERROR,
              severity: 'error',
            });
            setSendEndCallMessage(false);
          }
        );
    }
  }, [endCallMessage]);

  return (
    <Grow in={open}>
      <Paper
        id="chatbox"
        elevation={6}
        sx={{
          position: 'absolute',
          overflow: 'hidden',
          display: 'flex',
          bottom: '16px',
          right: '16px',
          height: '80%',
          zIndex: 1050,
        }}
      >
        {/* The composer's message field grows with what is typed, which it does by writing into a
            shadow field and reading its height back - a layout the browser settles synchronously.
            It is built when the chat is opened rather than kept ready behind it. */}
        {open && (
          <ChatBox
            updateItem={updateItem}
            campaignId={campaignId}
            subtractSmsNumber={() => {
              setCustomerUnreadMessages(customerUnreadMessages - 1);
            }}
            close={onClose}
            ObserverWrapper={ObserverWrapper}
            handleSendMessage={handleSendMessage}
            messages={chatMessages}
          />
        )}
      </Paper>
    </Grow>
  );
};

export default Chat;
